import React, { useState } from 'react';
import './BottomNav.css';

/**
 * BottomNav - Mobile bottom navigation with dynamic tabs and a floating action button
 *
 * Props:
 *   tabs: Array of { id, label, icon } — rendered as nav tabs
 *   activeTab: string — id of the currently active tab
 *   onTabChange: (tabId) => void
 *   onUploadTest: () => void
 *   onManualEntry: () => void
 *   uploadingState: boolean
 *   showFab: boolean — whether to show the floating action button (default true)
 *   pulseTabIds: string[] — tab ids to apply the demo strobe to (default [])
 *   demoDisabled: boolean — when true, the add-test FAB is rendered visibly disabled (demo)
 */
function BottomNav({
    tabs,
    activeTab,
    onTabChange,
    onUploadTest,
    onManualEntry,
    uploadingState = false,
    showFab = true,
    pulseTabIds = [],
    demoDisabled = false
}) {
    const [fabOpen, setFabOpen] = useState(false);

    const handleFabClick = () => {
        if (uploadingState || demoDisabled) return;
        setFabOpen(!fabOpen);
    };

    const handleUpload = () => {
        setFabOpen(false);
        onUploadTest();
    };

    const handleManual = () => {
        setFabOpen(false);
        onManualEntry();
    };

    return (
        <>
            {/* Backdrop when FAB is open */}
            {fabOpen && (
                <div
                    className="fab-backdrop"
                    onClick={() => setFabOpen(false)}
                />
            )}

            {/* Floating Action Button with Speed Dial */}
            {showFab && (
                <div className={`fab-container ${fabOpen ? 'open' : ''}`}>
                    {fabOpen && (
                        <div className="fab-speed-dial">
                            <button
                                className="fab-option"
                                onClick={handleUpload}
                                disabled={uploadingState}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                <span>Upload Test</span>
                            </button>
                            <button
                                className="fab-option"
                                onClick={handleManual}
                                disabled={uploadingState}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                                <span>Manual Entry</span>
                            </button>
                        </div>
                    )}
                    <button
                        className={`fab-button ${fabOpen ? 'open' : ''} ${uploadingState || demoDisabled ? 'disabled' : ''}`}
                        onClick={handleFabClick}
                        disabled={uploadingState || demoDisabled}
                        title={demoDisabled ? 'Demo — not available' : undefined}
                        aria-label={fabOpen ? 'Close menu' : 'Add test'}
                    >
                        {uploadingState ? (
                            <div className="fab-spinner" />
                        ) : (
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                className="fab-icon"
                            >
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        )}
                    </button>
                </div>
            )}

            {/* Bottom Navigation Bar */}
            <nav className="bottom-nav">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`nav-tab ${activeTab === tab.id ? 'active' : ''}${pulseTabIds.includes(tab.id) ? ' demo-pulse' : ''}`}
                        onClick={() => onTabChange(tab.id)}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>
                ))}
            </nav>
        </>
    );
}

export default BottomNav;
